import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { MultiSelectTriggerLabel } from './multi-select.label';

let nextMultiSelectId = 0;

/**
 * The rail's checkbox dropdown — the mockup's `AGUMultiSelect` (`.msel`,
 * grants.html:201-214 and :532-590): a full-width trigger reading the all-label
 * or `first +N` with a count badge, a floating `role="group"` panel of
 * checkboxes capped at 248px, and a `Clear selection` footer. One panel open
 * at a time is enforced the same way the sort trigger does it — Escape and an
 * outside click close it.
 *
 * Per-option counts are not rendered: the mockup computes them from its
 * in-page array and no facet endpoint carries them (D-F6).
 */
@Component({
    standalone: false,
    selector: 'agu-multi-select',
    templateUrl: './multi-select.component.html',
    styleUrl: './multi-select.component.scss',
})
export class AGUMultiSelectComponent {
    @Input() Options: readonly string[] = [];
    @Input() Selected: readonly string[] = [];
    /** Trigger copy while nothing is selected, e.g. `All funder types`. */
    @Input({ required: true }) AllLabel!: string;
    /** Accessible name of the panel, e.g. `Funder type`. */
    @Input({ required: true }) GroupLabel!: string;
    /**
     * The id of the caller's own visual heading for this field (e.g. the
     * rail's `<span id="ra-grant-filter-type-label">Funder type</span>`).
     * When set, the trigger's accessible name is composed from that heading
     * plus the trigger's own dynamic label (`aria-labelledby` concatenation),
     * e.g. "Funder type All funder types" — without it the heading is purely
     * visual and the trigger's only accessible name is its own content.
     */
    @Input() LabelledBy: string | null = null;
    @Output() SelectedChange = new EventEmitter<string[]>();

    Open = false;
    private readonly id = (nextMultiSelectId += 1);
    readonly PanelId = `agu-msel-panel-${this.id}`;
    readonly LabelId = `agu-msel-label-${this.id}`;

    constructor(private readonly host: ElementRef<HTMLElement>) {}

    get TriggerLabel(): string {
        return MultiSelectTriggerLabel(this.Selected, this.AllLabel);
    }

    IsSelected(value: string): boolean {
        return this.Selected.includes(value);
    }

    Toggle(): void {
        this.Open = !this.Open;
    }

    ToggleValue(value: string, event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        const without = this.Selected.filter((v) => v !== value);
        this.SelectedChange.emit(target.checked ? [...without, value] : without);
    }

    Clear(): void {
        if (this.Selected.length > 0) this.SelectedChange.emit([]);
    }

    @HostListener('document:keydown.escape') OnEscape(): void {
        this.Open = false;
    }

    @HostListener('document:click', ['$event']) OnDocumentClick(event: MouseEvent): void {
        if (!this.Open) return;
        const target = event.target;
        if (target instanceof Node && this.host.nativeElement.contains(target)) return;
        this.Open = false;
    }
}
